import { useRef, useEffect, useState } from 'react';
import type { ElementPatch } from '@/components/canvas/document/publicTypes';

export interface RadiusDragState {
  id: string;
  corner: 'tl' | 'tr' | 'br' | 'bl';
  startRadius: number;
  startClientX: number;
  startClientY: number;
  startRectX: number;
  startRectY: number;
  startWidth: number;
  startHeight: number;
}

export function useRadiusDrag(
  pageRef: React.RefObject<HTMLDivElement | null>,
  zoom: number,
  updateElementSilent: (id: string, patch: ElementPatch) => void,
  commitElement: (id: string, patch: ElementPatch) => void,
) {
  const [radiusDragState, setRadiusDragState] = useState<RadiusDragState | null>(null);
  const [radiusPreview, setRadiusPreview] = useState<number | null>(null);
  const radiusPreviewRef = useRef<number | null>(null);

  useEffect(() => {
    if (!radiusDragState) return;
    const { id, corner, startRectX, startRectY, startWidth, startHeight } = radiusDragState;
    const maxRadius = Math.max(0, Math.min(startWidth, startHeight) / 2);
    const pageEl = pageRef.current;
    if (!pageEl) return;
    const onMove = (e: MouseEvent) => {
      const pageRect = pageEl.getBoundingClientRect();
      const mx = (e.clientX - pageRect.left) / zoom;
      const my = (e.clientY - pageRect.top) / zoom;
      let cx: number, cy: number, dx: number, dy: number;
      if (corner === 'tl') { cx = startRectX; cy = startRectY; dx = 1; dy = 1; }
      else if (corner === 'tr') { cx = startRectX + startWidth; cy = startRectY; dx = -1; dy = 1; }
      else if (corner === 'br') { cx = startRectX + startWidth; cy = startRectY + startHeight; dx = -1; dy = -1; }
      else { cx = startRectX; cy = startRectY + startHeight; dx = 1; dy = -1; }
      const vx = mx - cx, vy = my - cy, len = Math.sqrt(dx * dx + dy * dy);
      const ux = dx / len, uy = dy / len, t = vx * ux + vy * uy;
      const radiusAlongDiagonal = t / Math.SQRT2;
      const rVal = Math.round(Math.max(0, Math.min(maxRadius, radiusAlongDiagonal)));
      radiusPreviewRef.current = rVal;
      setRadiusPreview(rVal);
      updateElementSilent(id, { appearance: { radius: { all: rVal } } });
    };
    const cursors: Record<string, string> = { tl: 'nwse-resize', tr: 'nesw-resize', br: 'nwse-resize', bl: 'nesw-resize' };
    const prev = document.body.style.cursor;
    document.body.style.cursor = cursors[corner];
    const onUp = () => {
      // 提交最终圆角，生成历史记录并清除 _pendingBase
      if (radiusPreviewRef.current !== null) {
        commitElement(id, { appearance: { radius: { all: radiusPreviewRef.current } } });
      }
      document.body.style.cursor = prev;
      setRadiusDragState(null);
      setRadiusPreview(null);
      radiusPreviewRef.current = null;
    };
    const opts = { capture: true };
    document.addEventListener('mousemove', onMove, opts);
    document.addEventListener('mouseup', onUp, opts);
    return () => { document.removeEventListener('mousemove', onMove, opts); document.removeEventListener('mouseup', onUp, opts); };
  }, [radiusDragState, zoom, updateElementSilent, commitElement, pageRef]);

  return { radiusDragState, setRadiusDragState, radiusPreview, setRadiusPreview, radiusPreviewRef };
}
